<template>
  <div class="categories-page">
    <div class="filter-bar">
      <div class="search-box">
        <input 
          type="text" 
          v-model="searchKey" 
          placeholder="搜索分类名称..."
          class="search-input"
        />
        <button @click="search" class="search-btn">搜索</button>
      </div>
      <button @click="showAddModal = true" class="add-btn">+ 添加分类</button>
    </div>

    <div class="table-container">
      <table class="categories-table">
        <thead>
          <tr>
            <th>分类ID</th>
            <th>分类名称</th>
            <th>图标</th>
            <th>层级</th>
            <th>排序</th>
            <th>状态</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="category in treeCategories" :key="category.id">
            <td>{{ category.id }}</td>
            <td>
              <span :style="{ paddingLeft: (category.level - 1) * 24 + 'px' }" class="tree-node">
                <span v-if="category.level > 1" class="tree-line">└─</span>
                {{ category.name }}
              </span>
            </td>
            <td>
              <img v-if="category.icon" :src="category.icon" class="icon-img" />
              <span v-else class="no-icon">-</span>
            </td>
            <td>{{ category.level === 1 ? '一级' : category.level === 2 ? '二级' : '三级' }}</td>
            <td>{{ category.sort_order }}</td>
            <td><span :class="'status ' + category.status">{{ category.status === 1 ? '启用' : '禁用' }}</span></td>
            <td>
              <button @click="editCategory(category)" class="action-btn edit">编辑</button>
              <button @click="toggleStatus(category)" class="action-btn" :class="category.status === 1 ? 'disable' : 'enable'">
                {{ category.status === 1 ? '禁用' : '启用' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pagination">
      <button :disabled="currentPage === 1" @click="currentPage--" class="page-btn">上一页</button>
      <span class="page-info">第 {{ currentPage }} / {{ totalPages }} 页</span>
      <button :disabled="currentPage === totalPages" @click="currentPage++" class="page-btn">下一页</button>
    </div>

    <div v-if="showAddModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3>{{ editingCategory ? '编辑分类' : '添加分类' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>* 分类名称（建议参考某团外卖的子分类设计）</label>
            <input v-model="form.name" type="text" class="form-control" placeholder="请输入分类名称" />
          </div>
          <div class="form-group">
            <label>父级分类</label>
            <select v-model.number="form.parent_id" class="form-control">
              <option :value="0">无（一级分类）</option>
              <option v-for="cat in parentOptions" :key="cat.id" :value="cat.id">
                {{ cat.level === 2 ? '└─ ' : '' }}{{ cat.name }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>当前层级</label>
            <select v-model.number="form.level" class="form-control" disabled>
              <option :value="1">一级分类</option>
              <option :value="2">二级分类</option>
              <option :value="3">三级分类</option>
            </select>
          </div>
          <div class="form-group">
            <label>图标URL（选填）</label>
            <input v-model="form.icon" type="text" class="form-control" placeholder="请输入图标地址" />
          </div>
          <div class="form-group">
            <label>分类描述（选填）</label>
            <input v-model="form.description" type="text" class="form-control" placeholder="请输入分类描述" />
          </div>
          <div class="form-group">
            <label>排序</label>
            <input v-model.number="form.sort_order" type="number" class="form-control" placeholder="数字越小越靠前" />
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeModal" class="btn-cancel">取消</button>
          <button @click="saveCategory" class="btn-save">保存</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCategoryList, createCategory, updateCategory } from '@/api/categories'

export default {
  name: 'Categories',
  data() {
    return {
      searchKey: '',
      currentPage: 1,
      totalPages: 10,
      showAddModal: false,
      editingCategory: null,
      form: {
        name: '',
        parent_id: 0,
        level: 1,
        icon: '',
        description: '',
        sort_order: 1
      },
      categories: []
    }
  },
  watch: {
    'form.parent_id'(newVal) {
      if (newVal === 0) {
        this.form.level = 1
      } else {
        const parent = this.categories.find(cat => cat && cat.id === newVal)
        this.form.level = parent ? parent.level + 1 : 2
      }
    }
  },
  computed: {
    parentOptions() {
      const filtered = this.categories.filter(cat => cat && cat.id !== undefined && cat.level < 3 && (!this.editingCategory || cat.id !== this.editingCategory.id))
      const result = []
      const level1 = filtered.filter(cat => cat && cat.level === 1).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      level1.forEach(l1 => {
        if (!l1) return
        result.push(l1)
        const level2 = filtered.filter(cat => cat && cat.parent_id === l1.id).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        level2.forEach(l2 => {
          if (!l2) return
          result.push(l2)
        })
      })
      return result
    },
    treeCategories() {
      const allCategories = this.categories.filter(cat => cat && cat.id !== undefined)
      const result = []
      const rootCategories = allCategories.filter(cat => cat && cat.parent_id === 0)
      
      const buildTree = (parentId, level) => {
        const children = allCategories.filter(cat => cat && cat.parent_id === parentId)
        children.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        
        children.forEach(child => {
          if (!child) return
          result.push({ ...child, level })
          buildTree(child.id, level + 1)
        })
      }
      
      rootCategories.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      rootCategories.forEach(root => {
        if (!root) return
        result.push({ ...root, level: 1 })
        buildTree(root.id, 2)
      })
      
      return result
    }
  },
  mounted() {
    this.loadCategories()
  },
  methods: {
    async loadCategories() {
      try {
        const params = { page: this.currentPage }
        if (this.searchKey.trim()) {
          params.keyword = this.searchKey.trim()
        }
        const response = await getCategoryList(params)
        if (response && response.data) {
          const pageData = response.data
          const items = pageData.data || pageData
          this.categories = Array.isArray(items) ? items.filter(cat => cat && cat.id !== undefined) : []
          this.totalPages = pageData.last_page || 10
        }
      } catch (error) {
      }
    },
    search() {
      this.currentPage = 1
      this.loadCategories()
    },
    getParentName(parentId) {
      if (parentId === 0) return '无'
      const parent = this.categories.find(cat => cat && cat.id === parentId)
      return parent ? parent.name : '未知'
    },
    editCategory(category) {
      this.editingCategory = category
      this.form.name = category.name
      this.form.parent_id = category.parent_id
      this.form.level = category.level
      this.form.icon = category.icon || ''
      this.form.description = category.description || ''
      this.form.sort_order = category.sort_order
      this.showAddModal = true
    },
    async toggleStatus(category) {
      const action = category.status === 1 ? '禁用' : '启用'
      if (confirm(`确定要${action}分类 ${category.name} 吗？`)) {
        try {
          const newStatus = category.status === 1 ? 0 : 1
          await updateCategory(category.id, { status: newStatus })
          category.status = newStatus
        } catch (error) {
          alert('更新状态失败')
        }
      }
    },
    closeModal() {
      this.showAddModal = false
      this.editingCategory = null
      this.form = {
        name: '',
        parent_id: 0,
        level: 1,
        icon: '',
        description: '',
        sort_order: 1
      }
    },
    async saveCategory() {
      if (!this.form.name.trim()) {
        alert('请输入分类名称')
        return
      }

      try {
        if (this.editingCategory) {
          await updateCategory(this.editingCategory.id, this.form)
          Object.assign(this.editingCategory, this.form)
          alert('更新成功')
        } else {
          const response = await createCategory(this.form)
          const newCategory = response.data.data
          if (newCategory && newCategory.id !== undefined) {
            this.categories.unshift(newCategory)
          }
          alert('添加成功')
        }
        this.closeModal()
        this.loadCategories()
      } catch (error) {
        alert('保存失败，请稍后重试')
      }
    }
  }
}
</script>

<style scoped>
.categories-page {
  padding: 20px;
}

.filter-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
}

.search-box {
  display: flex;
  gap: 10px;
}

.search-input {
  padding: 10px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  width: 250px;
}

.search-btn {
  padding: 10px 20px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.add-btn {
  padding: 10px 20px;
  background: #2ecc71;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.table-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  overflow: hidden;
}

.categories-table {
  width: 100%;
  border-collapse: collapse;
}

.categories-table th, .categories-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #eee;
  font-size: 13px;
}

.categories-table th {
  background: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.icon-img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.no-icon {
  color: #999;
}

.status {
  padding: 4px 0;
  border-radius: 20px;
  font-size: 12px;
}

.status.1 { background: #e8f5e9; color: #2ecc71; }
.status.0 { background: #ffebee; color: #e74c3c; }

.tree-node {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.tree-line {
  color: #999;
  font-size: 12px;
}

.action-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  margin-right: 5px;
}

.action-btn.edit {
  background: #3498db;
  color: #fff;
}

.action-btn.disable {
  background: #e74c3c;
  color: #fff;
}

.action-btn.enable {
  background: #2ecc71;
  color: #fff;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
}

.close-btn {
  font-size: 24px;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 15px 20px;
  border-top: 1px solid #eee;
}

.btn-cancel {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.btn-save {
  padding: 8px 16px;
  background: #3498db;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>